import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";

const filterA9TimeSeriesSchema = z.object({
  a9_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterA9TimeSeriesSchema = z.infer<typeof filterA9TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterA9TimeSeriesSchema>[] = [
  {
    name: "a9_static_datum_name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_contains", key: "isin", extract: "text", bucket: "equals" },
];

const fields: Field[] = [
  {
    name: "a9_static_datum_name_contains",
    label: `Actif`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_static_data",
    id_column: "a9_static_datum_id",
    column_name: "name",
    col_span: 2,
    filters: makeFiltersBuilder<FilterA9TimeSeriesSchema>(
      assetDepFields,
      "a9_static_datum_name_contains",
    ),
  },
  {
    name: "isin_contains",
    label: `ISIN`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_static_data",
    id_column: "a9_static_datum_id",
    column_name: "isin",
    filters: makeFiltersBuilder<FilterA9TimeSeriesSchema>(
      assetDepFields,
      "isin_contains",
    ),
  },
  {
    name: "currency_contains",
    label: `Devise`,
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "date",
    type: "dateRange",
    label: "Date de valeur",
    from: { name: "date_from" },
    to: { name: "date_to" },
    col_span: 2,
  },
  {
    name: "create",
    type: "dateRange",
    label: "Date de création",
    from: { name: "create_from" },
    to: { name: "create_to" },
    col_span: 2,
  },
];

export function FilterA9TimeSeries() {
  return (
    <FilterTable
      schema={filterA9TimeSeriesSchema}
      client="a9"
      tableName="a9_time_series"
      fields={fields}
      columns={2}
    />
  );
}
